import {Avatar, Button, Chip, Divider, Input, Link, Progress, Tooltip} from "@heroui/react";
import {useRecoilState} from 'recoil';
import {userState} from '../../store.jsx';
import {useForm} from '@mantine/form';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import repositories from '../../repositories.jsx';
import toast from 'react-hot-toast';
import {useEffect, useMemo, useRef} from 'react';
import _ from 'lodash';
import moment from 'moment';
import {Dropzone} from '@mantine/dropzone';
import {animate, motion, useMotionValue, useTransform} from 'framer-motion';
import PageLoader from '../../components/PageLoader.jsx';

const PAID_PLANS = [3, 5, 7];

/* Counts up 0 → value on mount and whenever the value changes */
function CountUp({value = 0, className}) {
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (v) => Math.round(v).toLocaleString());
    useEffect(() => {
        const controls = animate(mv, value, {duration: 1.1, ease: [0.16, 1, 0.3, 1]});
        return controls.stop;
    }, [value, mv]);
    return <motion.span className={className}>{rounded}</motion.span>;
}

/* A single stat tile in the overview strip */
function StatTile({icon, label, children, accent = 'violet', delay = 0}) {
    const tones = {
        violet: 'from-violet-500/10 to-fuchsia-500/10 ring-violet-200/70 text-primary',
        emerald: 'from-emerald-500/10 to-teal-500/10 ring-emerald-200/70 text-emerald-600',
        amber: 'from-amber-500/10 to-orange-500/10 ring-amber-200/70 text-amber-600',
        sky: 'from-sky-500/10 to-indigo-500/10 ring-sky-200/70 text-sky-600',
    };
    return (
        <motion.div
            initial={{opacity: 0, y: 14}}
            animate={{opacity: 1, y: 0}}
            transition={{type: 'spring', stiffness: 260, damping: 24, delay}}
            className={`relative overflow-hidden rounded-22 bg-gradient-to-br ${tones[accent]} ring-1 p-4 sm:p-5`}
        >
            <div className="flex items-center gap-2 mb-3">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-white/80 ring-1 ring-white shadow-sm">
                    <i className={`hgi-stroke ${icon} text-lg`}/>
                </span>
                <span className="text-tiny font-medium uppercase tracking-wide text-slate-500">{label}</span>
            </div>
            {children}
        </motion.div>
    );
}

export default function MyAccount() {
    const queryClient = useQueryClient();
    const openRef = useRef();
    const [authMe] = useRecoilState(userState);

    const form = useForm({
        initialValues: {name: '', photo: ''},
    });

    const authUserData = useQuery({
        queryKey: ['auth_account'],
        queryFn: () => repositories.auth.me().then((res) => res),
    });

    const user = authUserData?.data || authMe || {};

    useEffect(() => {
        form.setValues({
            name: authUserData?.data?.name,
            photo: _.get(authUserData?.data, 'avatar'),
        });
    }, [authUserData?.data]);

    const inputWrapper = 'min-h-12 bg-default-500/5 border-1 data-[focus=true]:!border-primary data-[hover=true]:border-primary shadow-none';

    const updateProfile = useMutation({
        mutationFn: () => repositories.auth.updateAccount(form.values),
        onSuccess: () => {
            toast.success('Your account successfully updated');
            queryClient.invalidateQueries({queryKey: ['auth_account']});
            queryClient.invalidateQueries({queryKey: ['auth']});
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || 'Something went wrong');
        },
    });

    const uploadAvatar = useMutation({
        mutationFn: repositories.auth.uploadAvatar,
        onSuccess: (response) => {
            toast.success('File successfully uploaded');
            form.setFieldValue('photo', response?.file_url);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.detail || 'Something went wrong');
        },
    });

    const onDrop = (file) => {
        const formData = new FormData();
        formData.append('file', _.head(file));
        uploadAvatar.mutate(formData);
    };

    const onReject = (fileRejections) => {
        const code = _.get(fileRejections, '0.errors.0.code');
        if (code === 'file-invalid-type') {
            toast.error('This file extension are not allow, try another image file');
        } else if (code === 'file-too-large') {
            toast.error('File too large, maximum file size is 5MB');
        } else {
            toast.error(_.get(fileRejections, '0.errors.0.message') || 'This file extension are not allow, try another image file');
        }
    };

    const isPaid = useMemo(() => _.includes(PAID_PLANS, user?.current_plan), [user?.current_plan]);
    const planLabel = user?.current_plan_name || (isPaid ? 'Pro Plan' : 'Free Trial');

    const slotTotal = Number(user?.voice_clone_slot ?? 0);
    const slotUsed = Number(user?.used_slot_by_user ?? 0);
    const slotPct = slotTotal > 0 ? Math.min(100, Math.round((slotUsed / slotTotal) * 100)) : 0;

    const isDirty = useMemo(
        () => (form.values?.name || '') !== (user?.name || '') || (form.values?.photo || '') !== (user?.avatar || ''),
        [form.values?.name, form.values?.photo, user?.name, user?.avatar]
    );

    const initials = (form.values?.name || user?.name || 'U')
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="relative">
            {authUserData.isLoading && (
                <div className="absolute left-0 top-0 w-full h-full bg-white/50 z-50 backdrop-blur-sm">
                    <PageLoader/>
                </div>
            )}

            {/* ── Hero banner ── */}
            <motion.div
                initial={{opacity: 0, y: -10}}
                animate={{opacity: 1, y: 0}}
                transition={{type: 'spring', stiffness: 220, damping: 24}}
                className="relative overflow-hidden rounded-30 p-6 sm:p-8 bg-gradient-to-br from-[#5A0AE0] via-[#7B2CE2] to-[#8C2CE2] text-white"
            >
                {/* aurora blobs */}
                <span aria-hidden className="pointer-events-none absolute -top-20 -left-10 h-56 w-56 rounded-full bg-fuchsia-400/30 blur-3xl"/>
                <span aria-hidden className="pointer-events-none absolute -bottom-24 right-0 h-64 w-64 rounded-full bg-indigo-300/25 blur-3xl"/>
                <span aria-hidden className="pointer-events-none absolute top-6 right-10 h-2 w-2 rounded-full bg-white/70 animate-pulse"/>

                <div className="relative flex flex-col sm:flex-row items-center sm:items-end gap-6">
                    {/* Avatar with hover-to-change overlay */}
                    <div className="relative group shrink-0">
                        <div className="rounded-full p-1 bg-white/20 ring-2 ring-white/40 backdrop-blur-sm">
                            <Avatar
                                key={_.toString(form.values?.photo)}
                                src={_.toString(form.values?.photo)}
                                name={initials}
                                classNames={{base: 'w-28 h-28', name: 'text-3xl font-semibold'}}
                            />
                        </div>
                        <Dropzone
                            openRef={openRef}
                            accept={['image/*']}
                            maxSize={5 * 1024 ** 2}
                            onDrop={onDrop}
                            draggable={false}
                            onReject={onReject}
                            activateOnClick={false}
                        >
                            <button
                                type="button"
                                onClick={() => openRef.current?.()}
                                className="absolute inset-1 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center text-white"
                            >
                                {uploadAvatar.isPending
                                    ? <i className="hgi-stroke hgi-loading-03 text-2xl animate-spin"/>
                                    : <span className="flex flex-col items-center gap-1 text-tiny font-medium">
                                        <i className="hgi-stroke hgi-camera-02 text-xl"/> Change
                                    </span>}
                            </button>
                        </Dropzone>
                    </div>

                    <div className="flex-1 text-center sm:text-left">
                        <div className="flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                            <h1 className="text-2xl sm:text-3xl font-semibold font-sans tracking-tight">
                                {user?.name || 'Your account'}
                            </h1>
                            <Chip
                                size="sm"
                                variant="flat"
                                className="bg-white/20 text-white border border-white/30"
                                startContent={<i className={`hgi-stroke ${isPaid ? 'hgi-crown' : 'hgi-sparkles'} text-sm ml-1`}/>}
                            >
                                {planLabel}
                            </Chip>
                        </div>
                        <p className="mt-1 text-white/80 flex items-center justify-center sm:justify-start gap-1.5">
                            <i className="hgi-stroke hgi-mail-01 text-sm"/> {user?.email}
                        </p>
                        <div className="mt-4 flex items-center justify-center sm:justify-start gap-2 flex-wrap">
                            <Button
                                as={Link} href="/user/plans"
                                size="sm" radius="full"
                                className="bg-white text-primary font-semibold"
                                startContent={<i className="hgi-stroke hgi-rocket-01 text-base"/>}
                            >
                                {isPaid ? 'Manage plan' : 'Upgrade'}
                            </Button>
                            <Button
                                as={Link} href="/contact-us"
                                size="sm" radius="full" variant="bordered"
                                className="text-white border-white/40"
                                startContent={<i className="hgi-stroke hgi-customer-support text-base"/>}
                            >
                                Support
                            </Button>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* ── Overview stats ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                <StatTile icon="hgi-coins-01" label="Available credits" accent="violet" delay={0.05}>
                    <CountUp value={Number(user?.available_credit || 0)} className="text-3xl font-semibold tabular-nums text-slate-800 font-sans"/>
                    <p className="mt-1 text-tiny text-slate-500">≈ {(Number(user?.available_credit || 0) / 2000).toFixed(1)} min of dubbing</p>
                </StatTile>

                <StatTile icon="hgi-mic-01" label="Voice clone slots" accent="emerald" delay={0.1}>
                    <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-semibold tabular-nums text-slate-800 font-sans">{slotUsed}</span>
                        <span className="text-slate-400 font-medium">/ {slotTotal || '—'}</span>
                    </div>
                    <Progress
                        aria-label="slots"
                        value={slotPct}
                        size="sm"
                        className="mt-2"
                        classNames={{indicator: 'bg-gradient-to-r from-emerald-500 to-teal-500'}}
                    />
                </StatTile>

                <StatTile icon="hgi-calendar-03" label={isPaid ? 'Next billing' : 'Trial status'} accent={isPaid ? 'sky' : 'amber'} delay={0.15}>
                    {isPaid ? (
                        <>
                            <p className="text-xl font-semibold text-slate-800 font-sans">
                                {user?.next_billing_date ? moment(user.next_billing_date).format('Do MMM, YYYY') : '—'}
                            </p>
                            <p className="mt-1 text-tiny text-slate-500">Credits reset on this date</p>
                        </>
                    ) : (
                        <>
                            <p className="text-xl font-semibold text-slate-800 font-sans capitalize">
                                {user?.trial_status === 'expired' ? 'Expired' : 'Active'}
                            </p>
                            <p className="mt-1 text-tiny text-slate-500">
                                {user?.trial_end_date ? `Ends ${moment(user.trial_end_date).format('Do MMM, YYYY')}` : 'Free trial in progress'}
                            </p>
                        </>
                    )}
                </StatTile>
            </div>

            {/* ── Edit details ── */}
            <motion.div
                initial={{opacity: 0, y: 14}}
                animate={{opacity: 1, y: 0}}
                transition={{type: 'spring', stiffness: 260, damping: 24, delay: 0.2}}
                className="mt-6 rounded-30 border border-slate-200/80 p-6 sm:p-8"
            >
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <h2 className="flex items-center gap-2 font-sans text-lg font-medium text-slate-800">
                        <i className="hgi-stroke hgi-user-edit-01 text-xl text-primary"/>
                        Account preferences
                    </h2>
                    {isDirty && (
                        <Chip size="sm" variant="flat" color="warning" className="font-medium">
                            Unsaved changes
                        </Chip>
                    )}
                </div>

                <Divider className="my-6"/>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 max-w-3xl">
                    <Input
                        type="text"
                        variant="bordered"
                        {...form.getInputProps('name')}
                        classNames={{inputWrapper}}
                        placeholder="Enter your fullname"
                        label="Fullname"
                        maxLength={50}
                        labelPlacement="outside"
                        startContent={<i className="hgi-stroke hgi-user text-slate-400"/>}
                    />
                    <Input
                        id="email"
                        type="email"
                        isReadOnly
                        variant="bordered"
                        value={user?.email || ''}
                        classNames={{inputWrapper}}
                        placeholder="Enter your email"
                        label="Email"
                        labelPlacement="outside"
                        startContent={<i className="hgi-stroke hgi-mail-01 text-slate-400"/>}
                        endContent={
                            <Tooltip content="Email can't be changed" color="foreground" showArrow>
                                <i className="hgi-stroke hgi-lock text-slate-400 cursor-default"/>
                            </Tooltip>
                        }
                    />
                </div>

                <Divider className="my-6"/>

                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="flat"
                            className="font-sans"
                            isLoading={uploadAvatar.isPending}
                            onPress={() => openRef.current?.()}
                            startContent={!uploadAvatar.isPending && <i className="hgi-stroke hgi-image-upload text-lg"/>}
                        >
                            Change photo
                        </Button>
                        {form.values?.photo && (
                            <Button
                                color="danger" variant="light" className="font-sans"
                                onPress={() => form.setFieldValue('photo', null)}
                                startContent={<i className="hgi-stroke hgi-delete-02 text-lg"/>}
                            >
                                Remove
                            </Button>
                        )}
                    </div>

                    <Button
                        isLoading={updateProfile.isPending}
                        isDisabled={!isDirty}
                        onPress={updateProfile.mutate}
                        color="primary" variant="shadow"
                        className="min-h-12 min-w-44 !scale-100 font-sans"
                        startContent={!updateProfile.isPending && <i className="hgi-stroke hgi-tick-02 text-xl"/>}
                    >
                        Save changes
                    </Button>
                </div>
            </motion.div>
        </div>
    );
}
